import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * PWAInstallContext — captures the `beforeinstallprompt` event so any
 * component (Topbar, ShareAppButton, install banner) can offer the install
 * action without losing the prompt to a stale handler.
 *
 * Persists a "dismissed-at" timestamp in localStorage so the floating banner
 * doesn't keep nagging the user — re-appears after 7 days.
 */

const DISMISS_KEY = "dc_pwa_dismiss_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const PWAInstallContext = createContext({
  canInstall: false,
  isStandalone: false,
  install: async () => false,
  shouldShowBanner: false,
  dismissBanner: () => {},
});

function detectStandalone() {
  if (typeof window === "undefined") return false;
  // iOS: navigator.standalone, modern browsers: matchMedia.
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isDismissed() {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(detectStandalone);
  const [bannerDismissed, setBannerDismissed] = useState(isDismissed);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
    };
    const onModeChange = () => setStandalone(detectStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    const mql = window.matchMedia?.("(display-mode: standalone)");
    mql?.addEventListener?.("change", onModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      mql?.removeEventListener?.("change", onModeChange);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return result?.outcome === "accepted";
    } catch {
      return false;
    }
  }, [deferredPrompt]);

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setBannerDismissed(true);
  }, []);

  const value = useMemo(
    () => ({
      canInstall: !!deferredPrompt && !standalone,
      isStandalone: standalone,
      install,
      shouldShowBanner:
        !!deferredPrompt && !standalone && !bannerDismissed,
      dismissBanner,
    }),
    [deferredPrompt, standalone, bannerDismissed, install, dismissBanner]
  );

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
    </PWAInstallContext.Provider>
  );
}

export function usePWAInstall() {
  return useContext(PWAInstallContext);
}
